import type {
  CartFragment,
  ConfigurableProductDetailsFragment,
  SimpleProduct,
  SimpleProductDetailsFragment,
} from '../graphql/magento'
import { AUTO_SHIP_DISCOUNT_RATE } from '../lib/cart/item-control-options'
import { CartItemVirtualProduct } from '../lib/cart/state/types'

export const formatAsCurrency = ({
  amount,
  fractionDigits,
  ignoreFalsyAmount,
}: {
  amount: number
  fractionDigits?: number | undefined
  ignoreFalsyAmount?: boolean
}): string => {
  const digitsWithDefault = typeof fractionDigits === 'number' ? fractionDigits : 2

  if (ignoreFalsyAmount ? Number(amount) : !isNaN(Number(amount))) {
    const amountSign = Math.sign(amount)
    if (amountSign === -1) {
      return `-${`$${Number(Math.abs(amount)).toFixed(digitsWithDefault)}`}`
    }

    return `$${Number(amount).toFixed(digitsWithDefault)}`
  }

  return ''
}

export const formatAsCurrencyWithCommas = ({
  amount,
  fractionDigits,
}: {
  amount: number
  fractionDigits?: number | undefined
}): string => {
  const digitsWithDefault = typeof fractionDigits === 'number' ? fractionDigits : 2

  if (Number(amount)) {
    const amountSign = Math.sign(amount)
    const formattedAmount = Number(Math.abs(amount))
      .toFixed(digitsWithDefault)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    if (amountSign === -1) {
      return `-$${formattedAmount}`
    }

    return `$${formattedAmount}`
  }

  return ''
}

export const formatAsCurrencyOrFree = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amount) {
    return 'Free'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const formatAsCurrencyOrZero = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amount) {
    return '$0.00'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const formatAsCurrencyWithCommasOrZero = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  const formattedAmount = Number(Math.abs(amount))
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (amountSign === -1) {
    return `-$${formattedAmount}`
  }

  if (amountSign === 0 || !amount) {
    return '$0.00'
  }

  return `$${formattedAmount}`
}

export const dropZeroCents = (amount: string): string => {
  if (amount.endsWith('.00')) {
    return amount.slice(0, -3)
  }
  return amount
}

export const dropZeroCentsAndSymbol = (amount: string): string => {
  let result = amount
  if (result.startsWith('$')) {
    result = result.slice(1)
  }
  if (result.endsWith('.00')) {
    result = result.slice(0, -3)
  }
  return result
}

export const formatAsCurrencyOrEmpty = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amountSign) {
    return '–'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const rawItemPrice = ({ price, qty }: { price: number; qty: number }): number => price * qty

/**
 * Estimated price after the Subscribe & Save (auto-ship) discount. Display-only: the authoritative
 * discounted total is applied server-side by Magento and shown in cart/checkout.
 */
export const autoShipDiscountedAmount = ({
  amount,
  discountRate = AUTO_SHIP_DISCOUNT_RATE,
}: {
  amount: number
  discountRate?: number
}): number => amount * (1 - discountRate)

export const itemQuantityAmount = ({
  item,
  qty,
}: {
  item: Pick<SimpleProduct, '__typename' | 'price_tiers'> | undefined | null
  qty: number
}): number => {
  const price =
    item?.__typename === 'SimpleProduct' ? item.price_tiers?.[0]?.final_price?.value || 0 : 0

  return rawItemPrice({ price, qty })
}

export const itemQuantityPrice = ({
  item,
  qty,
  ignoreFalsyAmount,
}: {
  item: Pick<SimpleProduct, '__typename' | 'price_tiers'> | undefined | null
  qty: number
  ignoreFalsyAmount?: boolean
}): string => formatAsCurrency({ amount: itemQuantityAmount({ item, qty }), ignoreFalsyAmount })

/**
 * The tier price amount `itemPrice` displays. Anything compared against a displayed item price
 * (e.g. a bundle compare-at basis) must read this same amount so the two cannot drift.
 */
export const itemPriceAmount = (
  item: ConfigurableProductDetailsFragment | SimpleProductDetailsFragment | undefined | null,
): number | undefined =>
  item?.__typename === 'SimpleProduct' && 'price_tiers' in item
    ? item.price_tiers?.[0]?.final_price?.value ?? undefined
    : undefined

export const itemPrice = ({
  item,
  ignoreFalsyAmount,
}: {
  item: ConfigurableProductDetailsFragment | SimpleProductDetailsFragment | undefined | null
  ignoreFalsyAmount?: boolean
}): string =>
  formatAsCurrency({
    amount: itemPriceAmount(item) || 0,
    ignoreFalsyAmount,
  })

// TODO: Update to use proper price ranges
// export const itemPriceRangeOrPrice = ({ item }) => {
//   const maximumPrice = item?.price_range?.maximum_price?.regular_price?.value

//   const minimumPrice = item?.price_range?.minimum_price?.regular_price?.value

//   if (minimumPrice && maximumPrice) {
//     return maximumPrice === minimumPrice
//       ? formatAsCurrency({ amount: minimumPrice })
//       : `${formatAsCurrency({ amount: minimumPrice })} - ${formatAsCurrency({
//           amount: maximumPrice,
//         })}`
//   }

//   if (maximumPrice) {
//     return formatAsCurrency({ amount: maximumPrice })
//   }

//   if (minimumPrice) {
//     return formatAsCurrency({ amount: minimumPrice })
//   }

//   return ''
// }

export const isItemPriceZero = (
  selectedVariant: SimpleProductDetailsFragment | null | undefined,
): boolean =>
  !rawItemPrice({
    price:
      selectedVariant?.__typename === 'SimpleProduct'
        ? selectedVariant.price_tiers?.[0]?.final_price?.value || 0
        : 0,
    qty: 1,
  })

// CART Helpers

export const cartSubtotal = ({ items, prices }: Pick<CartFragment, 'items' | 'prices'>): string => {
  if (items?.length) {
    return formatAsCurrency({ amount: prices?.subtotal_excluding_tax?.value || 0 })
  }

  return formatAsCurrencyOrEmpty({ amount: 0 })
}

export const shippingTotal = ({
  shippingAddresses,
  items,
}: {
  shippingAddresses: CartFragment['shipping_addresses']
  items: CartFragment['items']
}): string => {
  if (shippingAddresses.length && shippingAddresses[0]?.selected_shipping_method && items?.length) {
    return formatAsCurrencyOrFree({
      amount: shippingAddresses[0].selected_shipping_method.amount.value || 0,
    })
  }

  return '–'
}

export const salesTax = ({ prices }: Pick<CartFragment, 'prices'>): string => {
  const amount = (prices?.applied_taxes || []).reduce<number>(
    (acc, appliedTax) => acc + (appliedTax?.amount?.value || 0),
    0,
  )
  return formatAsCurrencyOrEmpty({ amount })
}

export const taxType = ({ prices }: Pick<CartFragment, 'prices'>): string => {
  const [appliedTax] = prices?.applied_taxes || []
  if (!appliedTax?.label || appliedTax.label === 'Tax') {
    return ''
  }
  return ` (${appliedTax.label})`
}

export const discountAmount = ({
  appliedCoupons,
  prices,
}: {
  appliedCoupons: CartFragment['applied_coupons']
  prices: CartFragment['prices']
}): string => {
  if (appliedCoupons == null || !appliedCoupons.length) {
    return formatAsCurrencyOrEmpty({ amount: 0 })
  }

  const amount = (prices?.discounts || []).reduce<number>((acc, discount) => {
    const isStoreCredit = discount?.label?.toLowerCase() === 'store credit'
    return isStoreCredit ? acc : acc + (discount?.amount.value || 0)
  }, 0)
  return formatAsCurrencyOrEmpty({
    amount,
  })
}

export const rebateAmount = (cart: CartFragment): string | null =>
  cart.rebate?.value ? formatAsCurrency({ amount: -Math.abs(cart.rebate.value) }) : null

export const cartTotal = ({ cart }: { cart: CartFragment }): string =>
  formatAsCurrencyOrZero({ amount: cart.prices?.grand_total?.value || 0 })

export const cartItemPrice = ({
  item,
}: {
  item: Exclude<CartFragment['items'], undefined | null>[0]
}): string =>
  formatAsCurrency({
    amount: item?.prices?.row_total.value || 0,
  })

export const cartItemAndDesignFeePrice = (
  item: Exclude<CartFragment['items'], undefined | null>[number],
  designFeeItem: CartItemVirtualProduct | undefined,
): string =>
  formatAsCurrency({
    amount: (item?.prices?.row_total.value || 0) + (designFeeItem?.prices?.row_total.value || 0),
  })

export const coloradoDeliveryFee = (cart: CartFragment): string | null => {
  const amount = cart.prices?.colorado_retail_delivery_fee?.value
  return amount ? formatAsCurrency({ amount }) : null
}
